import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getAuthCookies } from '@/features/cookies/auth-cookies';
import { useEventListener } from '@/features/event-handlers/useEventListener';
import { isDetachedSessionResumed } from '@/features/game/detached-session-resume';
import { useFgpGame } from '@/features/game/hooks/use-fgp-game';
import { ipAddressSelector } from '@/features/game/selectors/ip-selector';
import { gameDataSelector } from '@/features/game/store/game-data';
import { GameDetailsInput } from '@/features/game/types';
import { getProviderProduct } from '@/features/game-context/platform-helpers';
import { gameContextSelector } from '@/features/game-context/store/game-context';
import { Product, RGI } from '@/features/game-context/types';
import {
  geolocationJwtSelector,
  geolocationPreviouslyVerifiedSelector,
  geolocationVerifiedSelector,
} from '@/features/geolocation/store/geolocation';
import { GeolocationProduct } from '@/features/geolocation-update-token/types';
import { useUpdateToken } from '@/features/geolocation-update-token/use-update-token';
import { useIosBridgeLoader } from '@/features/wrapper-proxy/use-ios-bridge-loader';

export const useGame = () => {
  useEventListener();
  useIosBridgeLoader();
  const gameData = useSelector(gameDataSelector);
  const ipAddress = useSelector(ipAddressSelector);
  const geolocationVerified = useSelector(geolocationVerifiedSelector);

  const previouslyVerified = useSelector(geolocationPreviouslyVerifiedSelector);
  const { updateGeoToken } = useUpdateToken();

  const geolocationToken = useSelector(geolocationJwtSelector);

  const {
    brand,
    provider,
    providerRef,
    providerRefDesktop,
    providerRefMobile,
    region,
    rgi,
    gameUid,
    userId,
    directLaunchParams,
    jackpotMode,
    iosVersion,
  } = useSelector(gameContextSelector);

  const { authToken, loginToken } = getAuthCookies();

  const { fetchGameData: fetchFgpGameData } = useFgpGame();

  const gameDetailsInput: GameDetailsInput | undefined = useMemo(
    () =>
      geolocationVerified === true &&
      !previouslyVerified &&
      geolocationToken &&
      authToken &&
      loginToken &&
      userId &&
      brand &&
      provider &&
      (providerRefDesktop || providerRefMobile || providerRef) &&
      region &&
      ipAddress
        ? {
            authToken,
            loginToken,
            userId,
            brand,
            provider,
            providerRef,
            providerRefDesktop,
            providerRefMobile,
            region,
            ipAddress,
            gameUid,
            geolocationToken,
            directLaunchParams,
            jackpotMode,
          }
        : undefined,
    [
      geolocationVerified,
      authToken,
      loginToken,
      userId,
      brand,
      provider,
      providerRef,
      providerRefDesktop,
      providerRefMobile,
      region,
      ipAddress,
      gameUid,
      geolocationToken,
      previouslyVerified,
      directLaunchParams,
      jackpotMode,
    ],
  );

  const product: GeolocationProduct =
    getProviderProduct(provider) === Product.Poker
      ? (Product.Poker.toUpperCase() as GeolocationProduct)
      : (Product.Casino.toUpperCase() as GeolocationProduct);

  useEffect(() => {
    const shouldUpdateGeolocationToken =
      (previouslyVerified || Boolean(gameData?.launchDetails && iosVersion)) &&
      geolocationVerified &&
      rgi === RGI.FGP &&
      loginToken &&
      authToken &&
      ipAddress &&
      geolocationToken;

    if (shouldUpdateGeolocationToken) {
      updateGeoToken({
        loginToken,
        authToken,
        geolocationToken,
        ipAddress,
        product,
      });
    }
  }, [
    updateGeoToken,
    loginToken,
    authToken,
    geolocationToken,
    ipAddress,
    product,
    gameData?.launchDetails,
    geolocationVerified,
    iosVersion,
    rgi,
    previouslyVerified,
  ]);

  useEffect(() => {
    if (isDetachedSessionResumed()) {
      return;
    }

    if (gameDetailsInput && !gameData?.launchDetails) {
      if (rgi === RGI.FGP) {
        fetchFgpGameData(gameDetailsInput);
      }
    }
  }, [fetchFgpGameData, gameDetailsInput, rgi, gameData?.launchDetails]);

  return { gameData };
};
